import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import isSaveShortcut from './isSaveShortcut';
import {
  type SaveShortcutBoundaryRegistration,
  type SaveShortcutPriority,
  type SaveShortcutScopeRegistration,
  SaveShortcutRegistryContext,
} from './context';

type RegisteredBoundary = SaveShortcutBoundaryRegistration & {
  activatedAt: number;
};

type RegisteredScope = SaveShortcutScopeRegistration & {
  activatedAt: number;
  registeredAt: number;
};

const PRIORITY_ORDER: Record<SaveShortcutPriority, number> = {
  page: 0,
  modal: 1,
};

function SaveShortcutProvider({ children }: PropsWithChildren) {
  const boundariesRef = useRef(new Map<symbol, RegisteredBoundary>());
  const scopesRef = useRef(new Map<symbol, RegisteredScope>());
  const sequenceRef = useRef(0);
  const savingBoundariesRef = useRef(new Set<symbol>());
  const globalSavingRef = useRef(false);

  const nextSequence = useCallback(() => {
    sequenceRef.current += 1;
    return sequenceRef.current;
  }, []);

  const registerBoundary = useCallback(
    (boundary: SaveShortcutBoundaryRegistration) => {
      boundariesRef.current.set(boundary.id, {
        ...boundary,
        activatedAt: nextSequence(),
      });

      return () => {
        boundariesRef.current.delete(boundary.id);
      };
    },
    [nextSequence],
  );

  const registerScope = useCallback(
    (scope: SaveShortcutScopeRegistration) => {
      const registeredAt = nextSequence();
      scopesRef.current.set(scope.id, {
        ...scope,
        activatedAt: 0,
        registeredAt,
      });

      return () => {
        scopesRef.current.delete(scope.id);
      };
    },
    [nextSequence],
  );

  const activateScope = useCallback(
    (id: symbol) => {
      const scope = scopesRef.current.get(id);
      if (scope) {
        scope.activatedAt = nextSequence();
      }
    },
    [nextSequence],
  );

  useEffect(() => {
    const handleSaveShortcut = (event: globalThis.KeyboardEvent) => {
      if (!isSaveShortcut(event)) {
        return;
      }

      // Ctrl/Cmd+Enter may be used to confirm an IME candidate. Do not
      // prevent that commit and do not treat it as a save command.
      if (event.isComposing && event.key === 'Enter') {
        return;
      }

      // This listener is application-wide: the browser must never handle
      // Ctrl/Cmd+S as "Save page", even when no save scope is available.
      event.preventDefault();

      if (event.repeat || event.isComposing) {
        return;
      }

      const activeBoundaries = Array.from(boundariesRef.current.values())
        .filter((boundary) => boundary.active)
        .sort(
          (first, second) =>
            PRIORITY_ORDER[second.priority] - PRIORITY_ORDER[first.priority] ||
            second.activatedAt - first.activatedAt,
        );
      const boundary = activeBoundaries[0];

      if (!boundary) {
        const globalScopes = Array.from(scopesRef.current.values())
          .filter((scope) => scope.globalShortcut && !scope.disabled())
          .sort((first, second) => second.registeredAt - first.registeredAt);
        const globalScope = globalScopes[0];
        if (globalScope && !globalSavingRef.current) {
          globalSavingRef.current = true;
          void Promise.resolve()
            .then(globalScope.onSave)
            .catch((error: unknown) => {
              console.error('Unexpected global save shortcut error.', error);
            })
            .finally(() => {
              globalSavingRef.current = false;
            });
        }
        return;
      }

      if (savingBoundariesRef.current.has(boundary.id)) {
        return;
      }

      const enabledScopes = Array.from(scopesRef.current.values()).filter(
        (scope) => scope.boundaryId === boundary.id && !scope.disabled(),
      );

      let scopesToSave: RegisteredScope[];
      if (boundary.strategy === 'all') {
        scopesToSave = enabledScopes.sort(
          (first, second) => first.registeredAt - second.registeredAt,
        );
      } else {
        const activeScope = enabledScopes.sort(
          (first, second) =>
            second.activatedAt - first.activatedAt ||
            second.registeredAt - first.registeredAt,
        )[0];
        scopesToSave = activeScope ? [activeScope] : [];
      }

      if (scopesToSave.length === 0) {
        return;
      }

      savingBoundariesRef.current.add(boundary.id);
      void (async () => {
        try {
          for (const scope of scopesToSave) {
            try {
              await scope.onSave();
            } catch (error) {
              console.error('Unexpected save shortcut error.', error);
            }
          }
        } finally {
          savingBoundariesRef.current.delete(boundary.id);
        }
      })();
    };

    window.addEventListener('keydown', handleSaveShortcut, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleSaveShortcut, {
        capture: true,
      });
    };
  }, []);

  const registry = useMemo(
    () => ({ activateScope, registerBoundary, registerScope }),
    [activateScope, registerBoundary, registerScope],
  );

  return (
    <SaveShortcutRegistryContext.Provider value={registry}>
      {children}
    </SaveShortcutRegistryContext.Provider>
  );
}

export default SaveShortcutProvider;
